import React, { useState } from "react"
import { useHistory } from "react-router-dom"

import { Button, Chip, CircularProgress, Grid, InputAdornment, InputLabel, TextField, Typography } from "@material-ui/core"
import MessageTipsPopover from "@/pages/Admin/TrackableLinks/MessageTipsPopover"

import { Notification, Portlet } from "@/components"
import TrackableLinkGroupPermissionGate from "@/components/PermissionGate/TrackableLinkGroupPermissionGate"
import { TrackableLinkDataType } from "@/protocols/trackableLinks"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/TrackableLinks/CreateNewTrackableLink/CreateNewTrackableLinkForm/styles"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"
import ApiService from "@/services/Api"
import slugify from "slugify"
import { inboxAppUrl } from "@/config/url"

const CreateNewTrackableLinkForm: React.FC = () => {
	const history = useHistory()

	const [newLinkData, setNewLinkData] = useState<TrackableLinkDataType>({} as TrackableLinkDataType)
	const [isSlugEdited, setIsSlugEdited] = useState(false)
	const [loading, setLoading] = useState<boolean>(false)

	const { validation, triggerValidation, clearValidation } = useValidation()
	const customClasses = useCustomStyles()
	const classes = useStyles()

	const handleChangeNewTrackableLinkField = (value: string, field: keyof TrackableLinkDataType) => {
		setNewLinkData(lastState => ({
			...lastState,
			[field]: value
		}))

		const isTrackableLinkTitleFieldChange = field === "title"

		/*
			It was necessary to add this method here too, because the slug is assembled from the title (the client can edit it), that is, if there is a title, there is also a slug, so I need to clean up the slug validation here.
		*/
		if (isTrackableLinkTitleFieldChange && !isSlugEdited) {
			clearValidation("slug")

			setNewLinkData((lastState) => ({
				...lastState,
				slug: slugify(value.toLowerCase(), {
					remove: /[^a-zA-Z0-9\s-]/g,
					trim: true
				})
			}))
		}
	}

	const handleSlugChange = (value: string) => {
		setIsSlugEdited(true)
		setNewLinkData((lastState) => ({
			...lastState,
			slug: slugify(value.toLowerCase(), {
				remove: /[^a-zA-Z0-9\s-]/g,
				trim: true
			})
		}))
	}

	const handleCreateNewTrackableLink = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setLoading(true)
		try {
			const { data } = await ApiService.post<{ trackablelinkId: string }>(
				"/trackable-links", {
					title: newLinkData.title?.trim(),
					slug: newLinkData.slug?.trim(),
					description: newLinkData.description?.trim(),
					message: newLinkData.message?.trim()
				}
			)

			Notification.success({ message: "Link criado com sucesso" })
			setLoading(false)
			setNewLinkData({
				title: "",
				slug: "",
				description: "",
				message: ""
			} as TrackableLinkDataType)

			history.push(`/admin/conversion/tools/trackable-links/${data.trackablelinkId}/details?from=create`)

			return data.trackablelinkId
		} catch (error) {
			setLoading(false)
			Notification.error({ message: "Ocorreu um erro ao criar um link" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<form onSubmit={handleCreateNewTrackableLink}>
						<Grid container spacing={4} direction="column">
							<Grid item xs={12}>
								<Portlet>
									<Grid container spacing={4}>
										<Grid item xs={12}>
											<Grid container spacing={2}>
												<Grid item>
													<Grid container direction="column">
														<Grid item>
															<InputLabel htmlFor="title" className={customClasses.inputLabel}>Título</InputLabel>
														</Grid>
														<Grid item style={{ lineHeight: "14px" }}>
															<Typography variant="caption">Somente você verá isso, serve para facilitar a identificação</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item xs={12}>
													<TextField
														id="title"
														value={newLinkData.title}
														onChange={
															({ target }) => handleChangeNewTrackableLinkField(target.value?.trimStart(), "title")
														}
														fullWidth
														variant="outlined"
														helperText={validation.title}
														error={!!validation.title}
													/>
												</Grid>
											</Grid>
										</Grid>

										<Grid item xs={12}>
											<Grid container spacing={2}>
												<Grid item>
													<InputLabel htmlFor="slug" className={customClasses.inputLabel}>Slug</InputLabel>
												</Grid>
												<Grid item xs={12}>
													<TextField
														id="slug"
														value={newLinkData.slug}
														onChange={({ target }) => handleSlugChange(target.value?.trimStart())}
														fullWidth
														InputProps={{
															startAdornment: (
																<InputAdornment position="start" style={{ opacity: 0.5, marginRight: 0 }}>
																	{inboxAppUrl.trackableLinkURL}/
																</InputAdornment>
															)
														}}
														variant="outlined"
														helperText={validation.slug}
														error={!!validation.slug}
													/>
												</Grid>
											</Grid>
										</Grid>

										<Grid item xs={12}>
											<Grid container spacing={2}>
												<Grid item>
													<Grid container direction="column">
														<Grid item>
															<InputLabel htmlFor="description" className={customClasses.inputLabel}>
																Descrição
																<Chip
																	label="Opcional"
																	size="small"
																	className={classes.chip}
																/>
															</InputLabel>
														</Grid>
														<Grid item style={{ lineHeight: "14px" }}>
															<Typography variant="caption">
																Somente você verá isso, serve para facilitar a identificação
															</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item xs={12}>
													<TextField
														id="description"
														value={newLinkData.description}
														onChange={
															({ target }) => handleChangeNewTrackableLinkField(target.value?.trimStart(), "description")
														}
														multiline
														minRows={4}
														fullWidth
														variant="outlined"
														helperText={validation.description}
														error={!!validation.description}
													/>
												</Grid>
											</Grid>
										</Grid>

										<Grid item xs={12}>
											<Grid container spacing={2}>
												<Grid item>
													<Grid container direction="column">
														<Grid item>
															<InputLabel htmlFor="message" className={customClasses.inputLabel}>
																Mensagem inicial que será enviada ao WhatsApp de atendimento
															</InputLabel>
														</Grid>
														<Grid item direction="row" style={{ lineHeight: "14px" }}>
															<Typography variant="caption" style={{ marginRight: 4 }}>
																Uma mensagem bem estruturada reduz a chance do cliente alterá-la, garantindo o rastreamento correto do contato.
															</Typography>
															<MessageTipsPopover />
														</Grid>
													</Grid>
												</Grid>
												<Grid item xs={12}>
													<TextField
														id="message"
														value={newLinkData.message}
														onChange={
															({ target }) => handleChangeNewTrackableLinkField(target.value?.trimStart(), "message")
														}
														placeholder="Essa é a mensagem padrão que será enviada na conversa com o WhatsApp do seu cliente."
														multiline
														minRows={4}
														fullWidth
														variant="outlined"
														helperText={validation.message}
														disabled={loading}
														error={!!validation.message}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Portlet>
							</Grid>
							<Grid item xs={12}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item>
										<Button
											variant="text"
											color="primary"
											type="button"
											onClick={history.goBack}
											disabled={loading}
											className={customClasses.cancelButton}
										>
											VOLTAR
										</Button>
									</Grid>
									<Grid item>
										<TrackableLinkGroupPermissionGate action="create">
											<Button
												variant="contained"
												color="primary"
												type="submit"
												endIcon={loading ? <CircularProgress size={20} color={"inherit"} /> : null}
											>
												SALVAR
											</Button>
										</TrackableLinkGroupPermissionGate>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</form>
				</Grid>
			</Grid>
		</>
	)
}

export default CreateNewTrackableLinkForm
